/**
 * Theme mode context + provider.
 * Supports light / dark / system. Preference persisted in localStorage.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createAppTheme } from './createAppTheme';
import { lightTokens, darkTokens } from './tokens';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeModeCtx {
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  effective: 'light' | 'dark';
}

const ThemeModeContext = createContext<ThemeModeCtx>({
  mode: 'light',
  setMode: () => {},
  effective: 'light',
});

const STORAGE_KEY = 'ipx-theme-mode';

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeRaw] = useState<ThemeMode>(() => {
    try {
      return (localStorage.getItem(STORAGE_KEY) as ThemeMode) ?? 'light';
    } catch {
      return 'light';
    }
  });

  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
  const effective: 'light' | 'dark' = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  const theme = useMemo(() => createAppTheme(effective), [effective]);

  const setMode = useCallback((m: ThemeMode) => {
    setModeRaw(m);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage unavailable
    }
  }, [mode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effective);
    document.documentElement.style.colorScheme = effective;

    // Inject CSS custom properties for SCSS consumption
    const tokens = effective === 'dark' ? darkTokens : lightTokens;
    const root = document.documentElement.style;
    root.setProperty('--t-bg', tokens.bg);
    root.setProperty('--t-surface', tokens.surface);
    root.setProperty('--t-surface-subtle', tokens.surfaceSubtle);
    root.setProperty('--t-surface-muted', tokens.surfaceMuted);
    root.setProperty('--t-border', tokens.border);
    root.setProperty('--t-border-strong', tokens.borderStrong);
    root.setProperty('--t-border-subtle', tokens.borderSubtle);
    root.setProperty('--t-fg', tokens.fgPrimary);
    root.setProperty('--t-fg2', tokens.fgSecondary);
    root.setProperty('--t-fg3', tokens.fgTertiary);
    root.setProperty('--t-fg-muted', tokens.fgSecondary);
    root.setProperty('--t-fg-on-accent', tokens.fgOnAccent);
    root.setProperty('--t-accent', tokens.accent);
    root.setProperty('--t-accent-hover', tokens.accentHover);
    root.setProperty('--color-primary', tokens.accent);
    root.setProperty('--color-primary-hover', tokens.accentHover);
    root.setProperty('--t-accent-subtle', tokens.accentSubtle);
    root.setProperty('--t-accent-border', tokens.accentBorder);
    root.setProperty('--t-accent-text', tokens.accentText);
    root.setProperty('--t-accent-text-strong', tokens.accentTextStrong);
    root.setProperty('--t-danger', tokens.danger);
    root.setProperty('--t-danger-bg', tokens.dangerBg);
    root.setProperty('--t-danger-fg', tokens.dangerFg);
    root.setProperty('--t-danger-border', tokens.dangerBorder);
    root.setProperty('--t-success', tokens.success);
    root.setProperty('--t-success-bg', tokens.successBg);
    root.setProperty('--t-success-fg', tokens.successFg);
    root.setProperty('--t-success-border', tokens.successBorder);
    root.setProperty('--t-warning', tokens.warning);
    root.setProperty('--t-warning-bg', tokens.warningBg);
    root.setProperty('--t-warning-fg', tokens.warningFg);
    root.setProperty('--t-warning-border', tokens.warningBorder);
    root.setProperty('--t-info', tokens.info);
    root.setProperty('--t-info-bg', tokens.infoBg);
    root.setProperty('--t-info-fg', tokens.infoFg);
    root.setProperty('--t-info-border', tokens.infoBorder);
    // table tokens
    root.setProperty('--t-table-header-bg', tokens.tableHeaderBg);
    root.setProperty('--t-table-header-fg', tokens.tableHeaderFg);
    root.setProperty('--t-table-header-fg-active', tokens.tableHeaderFgActive);
    root.setProperty('--t-table-header-border', tokens.tableHeaderBorder);
    root.setProperty('--t-table-row-bg', tokens.tableRowBg);
    root.setProperty('--t-table-row-bg-hover', tokens.tableRowBgHover);
    root.setProperty('--t-table-row-border', tokens.tableRowBorder);
  }, [effective]);

  const ctx = useMemo(() => ({ mode, setMode, effective }), [mode, setMode, effective]);

  return (
    <ThemeModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export const useThemeMode = () => useContext(ThemeModeContext);
